<template>
  <div class="payment-details-wrapper">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Détails du paiement</h1>
        <p v-if="paymentData.payment_reference || paymentData.reference">
          Référence {{ paymentData.payment_reference || paymentData.reference }}
        </p>
      </div>
      <div class="header-actions">
        <button class="btn btn-secondary" @click="editPayment">
          <i class="bi bi-pencil"></i>
          <span>Modifier</span>
        </button>
        <button class="btn btn-danger" @click="deletePayment">
          <i class="bi bi-trash"></i>
          <span>Supprimer</span>
        </button>
      </div>
    </header>

    <section v-if="!loading && hasPayment" class="page-body fade-in">
      <div class="summary-card">
        <div class="summary-header">
          <h2>{{ paymentData.payment_reference || paymentData.reference }}</h2>
        </div>
        <div class="summary-grid">
          <div>
            <span class="label">Montant</span>
            <span class="value">{{ formatCurrency(paymentData.amount) }}</span>
          </div>
          <div>
            <span class="label">Date</span>
            <span class="value">{{
              formatDate(paymentData.date || paymentData.payment_date)
            }}</span>
          </div>
          <div>
            <span class="label">Vente</span>
            <span class="value">{{
              paymentData.seller_sale_reference ||
              paymentData.sale?.reference ||
              "—"
            }}</span>
          </div>
          <div v-if="paymentData.seller_reference">
            <span class="label">Vendeur</span>
            <span class="value">{{ paymentData.seller_reference }}</span>
          </div>
        </div>
      </div>

      <div class="info-grid">
        <div class="info-card">
          <h3>Informations vente</h3>
          <div class="info-line">
            <span class="label">Vente</span>
            <span class="value">{{
              paymentData.seller_sale_reference || "—"
            }}</span>
          </div>
          <div class="info-line" v-if="paymentData.seller_reference">
            <span class="label">Vendeur</span>
            <span class="value">{{ paymentData.seller_reference }}</span>
          </div>
          <div class="info-line" v-if="paymentData.user_reference">
            <span class="label">Utilisateur</span>
            <span class="value">{{ paymentData.user_reference }}</span>
          </div>
        </div>
        <div class="info-card" v-if="paymentData.remark">
          <h3>Remarque</h3>
          <p>{{ paymentData.remark }}</p>
        </div>
      </div>
    </section>

    <div v-else-if="loading" class="loading-state">
      <div class="loader"></div>
      <p>Chargement du paiement...</p>
    </div>

    <div v-else class="empty-state">
      <i class="bi bi-emoji-frown"></i>
      <h3>Paiement introuvable</h3>
      <p>Le paiement demandé semble avoir été supprimé.</p>
      <button class="btn btn-primary" @click="goToList">
        <i class="bi bi-credit-card"></i>
        <span>Voir les paiements</span>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PaymentShow",
  data() {
    return {
      loading: true,
    };
  },
  computed: {
    ...mapGetters("sellerPayment", {
      payment: "getSellerPayment",
    }),
    reference() {
      return this.$route.params.reference;
    },
    paymentData() {
      return this.payment || {};
    },
    hasPayment() {
      return Object.keys(this.paymentData).length > 0;
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    goToList() {
      this.$router.push({ name: "payments-list" });
    },
    editPayment() {
      this.$router.push({
        name: "payments-edit",
        params: { reference: this.reference },
      });
    },
    async deletePayment() {
      const confirmed = await this.$confirm({
        title: "Supprimer le paiement",
        message: "Êtes-vous sûr de vouloir supprimer ce paiement ?",
        confirmButtonText: "Supprimer",
        cancelButtonText: "Annuler",
      });
      if (!confirmed) return;
      try {
        await this.$store.dispatch("sellerPayment/destroy", this.reference);
        this.$toast.success("Paiement supprimé avec succès.");
        this.goToList();
      } catch (error) {
        console.error("Error deleting payment", error);
        this.$toast.error("Impossible de supprimer le paiement.");
      }
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    formatDate(value) {
      if (!value) return "—";
      try {
        return new Date(value).toLocaleDateString();
      } catch (error) {
        return value;
      }
    },
    async loadPayment() {
      this.loading = true;
      try {
        await this.$store.dispatch("sellerPayment/show", this.reference);
      } catch (error) {
        console.error("Error fetching payment", error);
        this.$toast.error("Impossible de récupérer le paiement.");
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadPayment();
  },
};
</script>

<style scoped>
.payment-details-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-card {
  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.08),
    rgba(147, 51, 234, 0.08)
  );
  border-radius: 20px;
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.summary-header h2 {
  margin: 0;
  font-size: 1.6rem;
  color: #0f172a;
}

.badge {
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
}

.summary-grid .label {
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.summary-grid .value {
  display: block;
  font-weight: 600;
  color: #0f172a;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.06);
}

.info-card h3 {
  margin: 0;
  font-size: 1.2rem;
  color: #0f172a;
}

.info-line {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  color: #475569;
}

.info-line .label {
  font-weight: 600;
  color: #64748b;
}

.info-line .value {
  font-weight: 600;
  color: #111827;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
  text-align: center;
}

.empty-state i {
  font-size: 3rem;
  color: #2563eb;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-secondary {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.btn-danger {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .payment-details-wrapper {
    padding: 1.5rem 1.5rem 4rem;
  }
}

@media (max-width: 768px) {
  .payment-details-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .header-actions {
    width: 100%;
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
  .summary-card {
    padding: 1.25rem;
  }
}
</style>
